import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concat, forkJoin, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { TimeBlockHttpTransformationService } from '../../../../features/calendar/components/time-block/http/time-block-http-transformation.service';
import { ITimeBlockComponentItem } from '../../../../features/calendar/components/time-block/types/time-block-component-item-types';
import {
  TimeBlockContentType,
  TimeBlockHttpResponseType,
} from '../../../../features/calendar/components/time-block/types/time-block-types';
import { DateTimeHelper } from '../../../../features/calendar/util/date-time-helper';
import { assert } from '../../../assert/assert';
import { TimeBlockQueryStrings } from '../../../constants-repository/api-parts-and-querystrings.constants';
import { HttpOperation } from '../../../enums/http-operation';
import { EndpointService } from '../../../services/endpoints/endpoint.service';
import { HttpErrorService } from '../../../services/http-error.service';
import * as fromTasksActions from '../../projects/tasks/tasks.actions';
import * as fromTimeBlockActions from './time-blocks.actions';

@Injectable()
export class TimeBlocksEffects {
  fetchTimeBlocks$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromTimeBlockActions.fetchTimeBlockList),
      map((params) => params.payload),
      switchMap((timeBlockRequestParams) => {
        const startStr = DateTimeHelper.dateToISO8601String(timeBlockRequestParams.startDate);
        const endStr = DateTimeHelper.dateToISO8601String(timeBlockRequestParams.endDate);
        const endpointTimeBlocks = this.endpointService.getTimeBlockSlug(HttpOperation.Get);
        const isBillable = timeBlockRequestParams.isBillable;
        const isInvoiced = timeBlockRequestParams.isInvoiced;

        let params = new HttpParams();

        params = startStr ? params.append('start_date', startStr) : params;
        params = endStr ? params.append('end_date', endStr) : params;

        timeBlockRequestParams.timeBlockTypes.forEach((contentType) => {
          params = params.append('type', TimeBlockQueryStrings.get(contentType));
        });

        if (isBillable !== null) {
          params = params.append('is_billable', isBillable);
        }

        if (isInvoiced !== null) {
          params = params.append('is_invoiced', isInvoiced);
        }

        timeBlockRequestParams.selectedUserIds.map((userId) => {
          params = params.append('user_id', userId);
        });

        return this.http
          .get<{ timeblocks: TimeBlockHttpResponseType }>(endpointTimeBlocks, { params })
          .pipe(
            map((response) => {
              const timeBlockArrayFlattened = [];
              const timeBlocks = response.timeblocks;
              const [
                projectTimeBlocksFixInterval,
                projectTimeBlocksVariableInterval,
                absenceTimeBlocksFixInterval,
                absenceTimeBlocksVariableInterval,
              ] = this.timeBlockHttpTransformationService.mapTimeBlockResponse(timeBlocks);

              timeBlockArrayFlattened.push(
                ...projectTimeBlocksFixInterval,
                ...projectTimeBlocksVariableInterval,
                ...absenceTimeBlocksFixInterval,
                ...absenceTimeBlocksVariableInterval,
              );

              return fromTimeBlockActions.setInitialTimeBlocks({
                payload: timeBlockArrayFlattened,
                storeInHistory: true,
              });
            }),
            catchError((error) => {
              return this.httpErrorService.handleError(
                'Time blocks',
                fromTimeBlockActions.fetchTimeBlockList,
                fromTimeBlockActions,
                error,
              );
            }),
          );
      }),
    );
  });

  fetchTaskTimeBlocks$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromTimeBlockActions.fetchTimeBlocksForTasks),
      map((action) => action.payload),
      switchMap((timeBlockRequestParams) => {
        const requests = timeBlockRequestParams.taskIds.map((taskId: number) => {
          let params = new HttpParams();
          params = params.append('task_id', taskId);
          params = params.append('type', TimeBlockQueryStrings.get(TimeBlockContentType.Project));
          params = params.append('is_invoiced', timeBlockRequestParams.isInvoiced);
          params = params.append('include_active', timeBlockRequestParams.includeActiveTimeBlock);

          const endpoint = this.endpointService.getTimeBlockSlug(HttpOperation.Get);
          return this.http.get<TimeBlockHttpResponseType>(endpoint, { params });
        });

        return forkJoin(requests).pipe(
          map((responseArr: any[]) => {
            const projectTimeBlocksFixIntervalTotal: ITimeBlockComponentItem[] = [];
            const projectTimeBlocksVariableIntervalTotal: ITimeBlockComponentItem[] = [];
            const absenceTimeBlocksFixIntervalTotal: ITimeBlockComponentItem[] = [];
            const absenceTimeBlocksVariableIntervalTotal: ITimeBlockComponentItem[] = [];

            responseArr.forEach((response) => {
              const timeBlocks = response.timeblocks;
              const [
                projectTimeBlocksFixInterval,
                projectTimeBlocksVariableInterval,
                absenceTimeBlocksFixInterval,
                absenceTimeBlocksVariableInterval,
              ] = this.timeBlockHttpTransformationService.mapTimeBlockResponse(timeBlocks);

              projectTimeBlocksFixIntervalTotal.push(...projectTimeBlocksFixInterval);
              projectTimeBlocksVariableIntervalTotal.push(...projectTimeBlocksVariableInterval);
              absenceTimeBlocksFixIntervalTotal.push(...absenceTimeBlocksFixInterval);
              absenceTimeBlocksVariableIntervalTotal.push(...absenceTimeBlocksVariableInterval);
            });
            return fromTimeBlockActions.setTimeBlocksForTask({
              payload: [
                ...projectTimeBlocksFixIntervalTotal,
                ...absenceTimeBlocksFixIntervalTotal,
                ...projectTimeBlocksVariableIntervalTotal,
                ...absenceTimeBlocksVariableIntervalTotal,
              ],
            });
          }),
          catchError((error) => {
            return this.httpErrorService.handleError(
              'Task-Timeblocks',
              fromTimeBlockActions.setTimeBlocksForTask,
              fromTimeBlockActions,
              error,
            );
          }),
        );
      }),
    );
  });

  fetchActiveTimerTimeBlock$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromTimeBlockActions.fetchActiveTimerTimeBlock),
      map((request) => request.payload),
      switchMap((userId) => {
        const endpoint = this.endpointService.getTimeBlockSlug(HttpOperation.Get);
        const params = new HttpParams()
          .append('active_timer_timeblock_only', 'true')
          .append('user_id', userId);

        return this.http.get<{ timeblocks: TimeBlockHttpResponseType }>(endpoint, { params }).pipe(
          map((response) => {
            const [projectFix, projectVar, absenceFix, absenceVar] =
              this.timeBlockHttpTransformationService.mapTimeBlockResponse(response.timeblocks);

            const flattened: ITimeBlockComponentItem[] = [
              ...projectFix,
              ...projectVar,
              ...absenceFix,
              ...absenceVar,
            ];

            assert(
              flattened.length === 0 || flattened.length === 1 || flattened[0] === null,
              'Too many active timer time blocks returned from server.',
            );

            const active = flattened[0] ?? null;

            return fromTimeBlockActions.setActiveTimerTimeBlock({ payload: active });
          }),
          catchError((error) => {
            // always resolve awaiting callers
            return concat(
              of(fromTimeBlockActions.setActiveTimerTimeBlock({ payload: null })),
              this.httpErrorService.handleError(
                'Active time block',
                fromTimeBlockActions.fetchActiveTimerTimeBlock,
                fromTimeBlockActions,
                error,
              ),
            );
          }),
        );
      }),
    ),
  );

  addTimeBlock$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromTimeBlockActions.addTimeBlock),
      map((action) => action.payload),
      mergeMap((timeBlock) => {
        this.checkTimeSchedule(timeBlock);

        const endpoint = this.endpointService.getTimeBlockSlug(
          HttpOperation.Post,
          timeBlock.timeBlockContentType,
        );
        const timeBlockModelHttp =
          this.timeBlockHttpTransformationService.toTimeBlockHTTPPost(timeBlock);
        let params = new HttpParams();
        params = params.append('type', TimeBlockQueryStrings.get(timeBlock.timeBlockContentType));

        return this.http.post<unknown>(endpoint, timeBlockModelHttp, { params }).pipe(
          map((response) => {
            const modifiedResponse =
              this.timeBlockHttpTransformationService.toTimeBlockModifiedResponse(
                response,
                timeBlock.timeBlockContentType,
                HttpOperation.Post,
              );
            return fromTimeBlockActions.timeBlockAdded({
              payload: {
                modifiedTimeBlockItems: modifiedResponse.modifiedTimeBlockItems,
                responseCode: modifiedResponse.responseCode,
              },
            });
          }),
          catchError((error) => {
            return this.httpErrorService.handleError(
              'Time blocks',
              fromTimeBlockActions.addTimeBlock,
              fromTimeBlockActions,
              error,
            );
          }),
        );
      }),
    );
  });

  updateTimeBlock$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromTimeBlockActions.updateTimeBlock),
      map((action) => action.payload),
      mergeMap((timeBlock) => {
        this.checkTimeSchedule(timeBlock);

        const endpoint = this.endpointService.getTimeBlockSlug(
          HttpOperation.Put,
          timeBlock.timeBlockModel.id,
        );
        const timeBlockModelHttp =
          this.timeBlockHttpTransformationService.toTimeBlockHTTPPut(timeBlock);

        let params = new HttpParams();
        params = params.append('type', TimeBlockQueryStrings.get(timeBlock.timeBlockContentType));

        return this.http.put<unknown>(endpoint, timeBlockModelHttp, { params }).pipe(
          map((response) => {
            const modifiedResponse =
              this.timeBlockHttpTransformationService.toTimeBlockModifiedResponse(
                response,
                timeBlock.timeBlockContentType,
                HttpOperation.Put,
              );

            return fromTimeBlockActions.timeBlockUpdated({
              payload: {
                originalTimeBlockItem: timeBlock,
                modifiedTimeBlockItems: modifiedResponse.modifiedTimeBlockItems,
                responseCode: modifiedResponse.responseCode,
              },
            });
          }),
          catchError((error) => {
            return this.httpErrorService.handleError(
              'Time blocks',
              fromTimeBlockActions.updateTimeBlock,
              fromTimeBlockActions,
              error,
            );
          }),
        );
      }),
    );
  });

  deleteTimeBlock$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromTimeBlockActions.deleteTimeBlock),
      map((action) => action.payload),
      mergeMap((timeBlockId) => {
        const endpoint = this.endpointService.getTimeBlockSlug(HttpOperation.Delete, timeBlockId);
        return this.http.delete<unknown>(endpoint).pipe(
          map(() => {
            return fromTimeBlockActions.timeBlockDeleted({
              payload: {
                deletedTimeBlockId: timeBlockId,
                responseCode: 200,
              },
            });
          }),
          catchError((error) => {
            return this.httpErrorService.handleError(
              'timeBlocks',
              fromTimeBlockActions.deleteTimeBlock,
              fromTasksActions,
              error,
            );
          }),
        );
      }),
    );
  });

  deleteMultipleTimeBlocks$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromTimeBlockActions.deleteMultipleTimeBlocks),
      map((action) => action.payload),
      mergeMap((timeBlockIds) => {
        const endpoint = this.endpointService.getTimeBlockSlugForBulkDelete();
        return this.http.delete<unknown>(endpoint, { body: { ids: timeBlockIds } }).pipe(
          map(() => {
            return fromTimeBlockActions.multipleTimeBlocksDeleted({
              payload: {
                deletedTimeBlockIds: timeBlockIds,
                responseCode: 200,
              },
            });
          }),
          catchError((error) => {
            return this.httpErrorService.handleError(
              'timeBlocks',
              fromTimeBlockActions.deleteTimeBlock,
              fromTasksActions,
              error,
            );
          }),
        );
      }),
    );
  });

  constructor(
    private readonly actions$: Actions,
    private readonly http: HttpClient,
    private readonly timeBlockHttpTransformationService: TimeBlockHttpTransformationService,
    private readonly endpointService: EndpointService,
    private readonly httpErrorService: HttpErrorService,
  ) {}

  private checkTimeSchedule(timeBlock: ITimeBlockComponentItem) {
    const invalidBlockEnd = DateTimeHelper.isSameTime(
      timeBlock.timeBlockModel.end,
      DateTimeHelper.endOfDay(timeBlock.timeBlockModel.end),
    );
    assert(
      !invalidBlockEnd,
      'A time portion of 23:59:59 is invalid. We only have a latest end times of 00:00:00. ',
    );

    const validFixSchedule =
      timeBlock.timeBlockModel.fixInterval === null ||
      (DateTimeHelper.isMidnight(timeBlock.timeBlockModel.start) &&
        DateTimeHelper.isMidnight(timeBlock.timeBlockModel.end));
    assert(validFixSchedule, 'Fix interval time blocks need a time portion of 00:00:00.');
  }
}
